'use client'

import dynamic from 'next/dynamic'
import { C, Callout, Exercise, Figure, H, Hl, Lesson, P, Quiz, Step, UL } from '../_components/lesson'
import * as EX from './exercises/ml'
import * as EX2 from './exercises/ml-extra'

const OverfitLab = dynamic(() => import('../_components/widgets/MLWidgets').then(m => m.OverfitLab), { ssr: false })

export default function Generalization() {
  return (
    <Lesson>
      <Step>
        <H>Memorising isn’t learning</H>
        <P>
          Imagine two students. One memorises every answer in last year’s exam. The other understands the subject. On last
          year’s exam, both score 100%. On <em>this</em> year’s exam, only one of them does well.
        </P>
        <P>
          Models face the same trap. What we actually care about is how well a model does on data it has <strong>never
          seen</strong> — that’s called <strong>generalisation</strong>. A model that fits its training data perfectly but fails
          on new data is <strong>overfitting</strong>.
        </P>
        <Exercise id="memorizer" title="A model that memorises" {...EX.memorizer} />
        <P>The memoriser is perfect on training data (error 0!) and worse than a simple line on test data.</P>
      </Step>

      <Step gate="q-degree">
        <H>Too simple, too complex, just right</H>
        <P>
          Below, the <Hl c="blue">blue</Hl> points are training data, generated from a hidden curve (dashed) plus noise. We fit a
          polynomial: degree 1 is a line, degree 2 a parabola, higher degrees can wiggle more. The{' '}
          <Hl c="green">green</Hl> circles are fresh test data from the same source.
        </P>
        <Figure n="6.7" wide caption="Raise the degree. Training error always falls — but test error falls, bottoms out, and then rises as the curve starts chasing noise.">
          <OverfitLab />
        </Figure>
        <Quiz
          id="q-degree"
          question="At degree 12, training error is tiny but test error is large. What’s happening?"
          options={[
            { label: 'The curve bends to pass through the noise in the training points, which doesn’t repeat in new data.', correct: true },
            { label: 'The model is too simple to capture the pattern.', why: 'That’s underfitting — high error on both training and test. Here training error is tiny.' },
            { label: 'There’s a bug in the test data.', why: 'The test data comes from exactly the same source. The model just memorised noise.' },
          ]}
          explain="Low degree: underfitting (too simple). High degree: overfitting (too flexible). The sweet spot is where test error is lowest."
        />
      </Step>

      <Step gate="q-reg">
        <H>Fighting overfitting</H>
        <ul className="gd-list">
          <li>
            <strong>Hold out test data.</strong> Never judge a model on the data it trained on. Split off a test set at the
            start and don’t touch it until the end.
          </li>
          <li>
            <strong>More data.</strong> Noise averages out; real patterns don’t. This is the biggest reason large models need so
            much data.
          </li>
          <li>
            <strong>Regularisation.</strong> Add a penalty to the loss for large parameters, so the model prefers simpler,
            smoother explanations. Try the <em>penalty λ</em> slider above at degree 12.
          </li>
          <li>
            <strong>Early stopping.</strong> Watch test loss during training and stop when it starts rising.
          </li>
        </ul>
        <Quiz
          id="q-reg"
          question="With degree 12, what does increasing the penalty λ do?"
          options={[
            { label: 'Smooths the curve, raising training error a bit but lowering test error.', correct: true },
            { label: 'Makes the curve wigglier.', why: 'The penalty discourages large coefficients, which are what create wild wiggles.' },
            { label: 'Has no effect on test error.', why: 'Drag the slider and watch the green test error readout.' },
          ]}
        />
      </Step>

      <Step gate="split">
        <H>Split your data</H>
        <Exercise
          id="split"
          title="trainTestSplit"
          {...EX.split}
          hints={[
            <>
              <C>const shuffled = random.shuffle(items)</C> — shuffle first, so the test set isn’t just the last rows (which
              might be special, e.g. the most recent).
            </>,
            <>
              Work out <C>nTest = Math.round(items.length * testFraction)</C>, then slice.
            </>,
          ]}
        />
      </Step>

      <Step gate={['q-val', 'cross-validate']}>
        <H>Choosing without cheating</H>
        <P>
          Models have settings you pick rather than learn: the polynomial degree, the learning rate, the penalty λ, how long to
          train. These are <strong>hyperparameters</strong>. If you pick them by checking which does best on the test set,
          you’ve quietly trained on the test set, and its score stops being honest. So split three ways:
        </P>
        <UL>
          <li>
            <strong>Training set</strong>: the model learns its parameters from this.
          </li>
          <li>
            <strong>Validation set</strong>: you compare settings on this, and pick the best.
          </li>
          <li>
            <strong>Test set</strong>: opened once, at the very end, for the final honest score.
          </li>
        </UL>
        <Quiz
          id="q-val"
          question="You try 50 different settings and report the best one’s test-set score. What’s wrong?"
          options={[
            { label: 'Picking the best of 50 on the test set makes that score optimistic — some of it is luck', correct: true },
            { label: 'Nothing, the test set is held out', why: 'It stopped being held out the moment it influenced which setting you chose.' },
            { label: '50 settings is too few', why: 'The number isn’t the issue — the more you try, the worse the leak gets.' },
          ]}
          explain="Use a validation set (or cross-validation) to choose, and the test set only to report."
        />
        <P>
          With little data, one validation set is a noisy judge. <strong>k-fold cross-validation</strong> fixes that: cut the
          data into k folds, train on k − 1 of them and validate on the one left out, rotate so each fold gets a turn, and
          average the k scores.
        </P>
        <Exercise
          id="cross-validate"
          title="folds and cvError"
          {...EX2.crossValidate}
          hints={[
            <>
              folds: make <C>k</C> empty arrays, then <C>{'out[i % k].push(i)'}</C> for each index.
            </>,
            <>
              cvError: for each fold <C>val</C>, the training indices are all the others. Fit with <C>fitPoly</C>, measure the
              mean squared error on <C>val</C>, and average over folds.
            </>,
          ]}
        />
        <Callout kind="why">
          Generalisation is the whole point. Large language models are evaluated on held-out text and on benchmarks they weren’t
          trained on — and “did the test data leak into training?” is one of the most important questions in AI evaluation.
        </Callout>
      </Step>
    </Lesson>
  )
}
